import { createFileRoute } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { z } from 'zod'

import { AuthErrorBanner, AuthTitle } from '~/components/auth-shell/AuthFormPrimitives'
import {
  SetPasswordForm,
  type SetPasswordCopy,
} from '~/components/auth-shell/SetPasswordForm'

/**
 * `/authentication/set-cms-password` — CMS-user invite password set surface.
 *
 * Identical to `/authentication/set-password` except for copy + post-submit
 * redirect target. CMS users land in the CMS app, NOT the admin app, after
 * activating per `02-authentication.md` §8.
 *
 * HUMAN-FLAG (CMS app URL):
 * The legacy Next admin app redirects CMS users to `/authentication/login`
 * with a message. This SPA can't host the CMS app at a relative path
 * (admin-dashboard SPA != CMS Next app), so we redirect to a hardcoded
 * `/cms` external URL on the same origin. If the CMS app lives on a
 * different subdomain (e.g. `cms.thekairos.app`), wire it through env as
 * `VITE_CMS_APP_URL` and read it via `~/env`. Until that env exists, this
 * route hands off to `/cms` on the current origin.
 */

const searchSchema = z.object({
  token: z.string().optional(),
  email: z.string().email().optional(),
})

const CMS_REDIRECT = '/cms'

const CMS_COPY: SetPasswordCopy = {
  title: 'Welcome to Kairos CMS',
  subtitle: 'Set your password to start authoring.',
  cta: 'Activate CMS account',
  redirectTo: CMS_REDIRECT,
}

export const Route = createFileRoute('/authentication/set-cms-password')({
  validateSearch: searchSchema,
  component: SetCmsPasswordPage,
})

function SetCmsPasswordPage(): ReactNode {
  const search = Route.useSearch()
  const [bannerError, setBannerError] = useState<string | null>(null)

  return (
    <>
      {!search.token ? (
        <InvalidTokenState />
      ) : (
        <SetPasswordForm
          copy={CMS_COPY}
          token={search.token}
          email={search.email}
          bannerError={bannerError}
          onError={setBannerError}
          onSuccess={() => {
            setBannerError(null)
            // CMS app lives outside the admin SPA's TanStack route tree, so
            // we hand off to a real URL rather than `navigate()`.
            window.location.assign(CMS_REDIRECT)
          }}
        />
      )}
    </>
  )
}

function InvalidTokenState(): ReactNode {
  return (
    <>
      <AuthTitle
        title="This invite is invalid or expired"
        subtitle="Reach out to your CMS admin to get a fresh activation link."
      />
      <AuthErrorBanner>
        We couldn&apos;t find an activation token in this URL.
      </AuthErrorBanner>
    </>
  )
}
